import type { ExtractedINEData } from "./types";
import type { NipDestinationDialCode } from "./types";
import { hasCurpFormat } from "../../../../utils/vault/curp";
import { MEXICAN_STATES } from "./constants";
import {
  BDC_ADDRESS_TOTAL_FORMAT_ERROR,
  getBdcStreetAddressProjection,
  normalizeBdcName,
} from "../../../../utils/vault/bdcValidationShared";

export { BDC_ADDRESS_TOTAL_FORMAT_ERROR } from "../../../../utils/vault/bdcValidationShared";

export const REQUIRED_FIELD_ERROR = "Este campo es obligatorio";
export const SECOND_SURNAME_NOT_PROVIDED = "NO PROPORCIONADO";

export const CURP_FORMAT_ERROR = "Favor de proporcionar un CURP válido";
export const DIAL_CODE_FORMAT_ERROR = "Selecciona una lada válida";
export const BIRTH_DATE_FORMAT_ERROR =
  "Usa una fecha válida en formato AAAA-MM-DD";
export const BDC_NAME_FORMAT_ERROR =
  "Los nombres juntos deben tener de 2 a 26 letras y espacios.";
export const BDC_SURNAME_FORMAT_ERROR =
  "Usa de 2 a 26 letras y espacios.";
export const BDC_RFC_REQUIRED_ERROR = "El RFC es obligatorio para continuar.";
export const BDC_RFC_FORMAT_ERROR =
  "Ingresa un RFC de persona física válido de 10 o 13 caracteres.";
export const BDC_STREET_FORMAT_ERROR =
  "La calle debe tener de 2 a 80 caracteres.";
export const BDC_EXTERIOR_NUMBER_FORMAT_ERROR =
  "El número exterior no puede exceder 10 caracteres.";
export const BDC_INTERIOR_NUMBER_FORMAT_ERROR =
  "El número interior no puede exceder 10 caracteres.";
export const BDC_NEIGHBORHOOD_FORMAT_ERROR =
  "La colonia debe tener de 2 a 65 caracteres.";
export const BDC_MUNICIPALITY_FORMAT_ERROR =
  "El municipio o ciudad debe tener de 2 a 65 caracteres.";
export const BDC_POSTAL_CODE_FORMAT_ERROR =
  "Ingresa un código postal de 5 dígitos.";
export const BDC_STATE_FORMAT_ERROR =
  "Selecciona una entidad federativa válida.";
export const NIP_DESTINATION_REVIEWED_FIELD = "nip_destination_reviewed";
export const NIP_DESTINATION_DIAL_CODE_FIELD = "nip_destination_dial_code";
export const DEFAULT_NIP_DESTINATION_DIAL_CODE: NipDestinationDialCode = "+52";

export function isBdcNaturalPersonSubfolder(slug?: string): boolean {
  return Boolean(
    slug && /^(tenant|guarantor)-/.test(slug) && slug.endsWith("-pf"),
  );
}

export function isSecondSurnameNotProvided(value: string): boolean {
  return value.trim().toUpperCase() === SECOND_SURNAME_NOT_PROVIDED;
}

const REQUIRED_IDENTITY_FIELDS: (keyof ExtractedINEData)[] = [
  "nombre",
  "apellidoPaterno",
  "apellidoMaterno",
  "curp",
  "genero",
];

const REQUIRED_ADDRESS_FIELDS: (keyof ExtractedINEData["direccion"])[] = [
  "calle",
  "numeroExterior",
  "colonia",
  "codigoPostal",
  "municipio",
  "estado",
];

type UnknownRecord = Record<string, unknown>;

export interface IDDataValidationOptions {
  readonly requireDialCode: boolean;
  readonly requireStrictCurp?: boolean;
  readonly requireExteriorNumber?: boolean;
  readonly requireBirthDate?: boolean;
  /** Validate names and PF address/RFC using the mutable BDC input contract. */
  readonly strictBdcValidation?: boolean;
}

const BDC_NAME_PATTERN = /^[A-Z ]{2,26}$/;
const BDC_PHYSICAL_PERSON_RFC_PATTERN =
  /^([A-ZÑ]{4}(\d{2})(0[1-9]|1[0-2])(0[1-9]|1\d|2\d|3[01]))([A-Z\d]{2}(?:A|\d))?$/;
const BDC_ZIP_CODE_PATTERN = /^\d{5}$/;
const COMMON_STATE_CODES = [
  "AGS",
  "BCN",
  "BCS",
  "CAM",
  "CHS",
  "CHI",
  "CDMX",
  "COA",
  "COL",
  "DGO",
  "EM",
  "GTO",
  "GRO",
  "HGO",
  "JAL",
  "MICH",
  "MOR",
  "NAY",
  "NL",
  "OAX",
  "PUE",
  "QRO",
  "QR",
  "SLP",
  "SIN",
  "SON",
  "TAB",
  "TAM",
  "TLAX",
  "VER",
  "YUC",
  "ZAC",
];
// Keep the frontend's accepted state names aligned with bdc_payload.py's
// MEXICO_STATES and EXTRA_MEXICO_STATE_ALIASES. The backend still normalizes
// the final value before building the Platform payload.
const EXTRA_MEXICO_STATE_ALIASES = [
  "BAJA CALIFORNIA NORTE",
  "BC",
  "CD DE MEXICO",
  "CD MEXICO",
  "DF",
  "DISTRITO FEDERAL",
  "EDO DE MEXICO",
  "EDO MEX",
  "ESTADO MEXICO",
  "MEXICO",
  "MEX",
  "CAMP",
  "CHIS",
  "CHIH",
  "COAH",
  "COAHUILA",
  "COAHUILA DE ZARAGOZA",
  "MICHOACAN",
  "MICHOACAN DE OCAMPO",
  "Q ROO",
  "QROO",
  "VERACRUZ",
  "VERACRUZ LLAVE",
  "VERACRUZ DE IGNACIO DE LA LLAVE",
  "TAMPS",
];

function normalizeBdcStateAlias(value: string): string {
  return normalizeBdcName(value).replace(/[^A-Z0-9]+/g, " ").trim();
}

export function isValidBdcStateAlias(value: string): boolean {
  const normalized = normalizeBdcStateAlias(value);
  return Boolean(normalized && VALID_BDC_STATES.has(normalized));
}

const VALID_BDC_STATES = new Set(
  [...MEXICAN_STATES, ...COMMON_STATE_CODES, ...EXTRA_MEXICO_STATE_ALIASES]
    .flatMap((state) => {
      const normalized = normalizeBdcStateAlias(state);
      return [normalized, normalized.replace(/\s/g, "")];
    })
    .filter(Boolean),
);

const ID_FIELD_ERROR_KEYS = new Set([
  "nombre",
  "segundoNombre",
  "apellidoPaterno",
  "apellidoMaterno",
  "curp",
  "rfc",
  "fechaNacimiento",
  "genero",
  "direccion.calle",
  "direccion.numeroExterior",
  "direccion.numeroInterior",
  "direccion.colonia",
  "direccion.codigoPostal",
  "direccion.municipio",
  "direccion.estado",
  "email",
  "phone_number",
  "dial_code",
]);

function isValidBirthDate(value: string): boolean {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const isoTimestamp =
    /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.exec(
      value,
    );
  const legacy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!iso && !isoTimestamp && !legacy) return false;
  if (isoTimestamp && Number.isNaN(Date.parse(value))) return false;
  const calendarDate = iso ?? isoTimestamp;
  const year = Number(calendarDate ? calendarDate[1] : legacy![3]);
  const month = Number(calendarDate ? calendarDate[2] : legacy![2]);
  const day = Number(calendarDate ? calendarDate[3] : legacy![1]);
  if (year < 1) return false;
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** Keep the national number separate from the supported NIP dial codes. */
export function normalizeNipPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("52")) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
}

export function isValidNipDestinationDialCode(
  value: unknown,
): value is NipDestinationDialCode {
  return value === "+52" || value === "+1";
}

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function readRequiredValue(record: UnknownRecord, field: string): string {
  return String(record[field] ?? "").trim();
}

function validateBdcAddress(
  direccion: UnknownRecord,
  errors: Record<string, string>,
): void {
  const lengthRules: [
    keyof ExtractedINEData["direccion"],
    number,
    number,
    string,
  ][] = [
    ["calle", 2, 80, BDC_STREET_FORMAT_ERROR],
    ["colonia", 2, 65, BDC_NEIGHBORHOOD_FORMAT_ERROR],
    ["municipio", 2, 65, BDC_MUNICIPALITY_FORMAT_ERROR],
  ];

  for (const [field, min, max, message] of lengthRules) {
    const value = readRequiredValue(direccion, field);
    if (value && (value.length < min || value.length > max)) {
      errors[`direccion.${field}`] = message;
    }
  }

  for (const field of ["numeroExterior", "numeroInterior"] as const) {
    const value = readRequiredValue(direccion, field);
    if (value.length > 10) {
      errors[`direccion.${field}`] =
        field === "numeroExterior"
          ? BDC_EXTERIOR_NUMBER_FORMAT_ERROR
          : BDC_INTERIOR_NUMBER_FORMAT_ERROR;
    }
  }

  const streetAddress = getBdcStreetAddressProjection(direccion, [
    "calle",
    "numeroExterior",
    "numeroInterior",
  ]);
  if (streetAddress.length > 80) {
    errors["direccion.calle"] = BDC_ADDRESS_TOTAL_FORMAT_ERROR;
  }

  const postalCode = readRequiredValue(direccion, "codigoPostal");
  if (postalCode && !BDC_ZIP_CODE_PATTERN.test(postalCode)) {
    errors["direccion.codigoPostal"] = BDC_POSTAL_CODE_FORMAT_ERROR;
  }

  const state = readRequiredValue(direccion, "estado");
  if (state && !isValidBdcStateAlias(state)) {
    errors["direccion.estado"] = BDC_STATE_FORMAT_ERROR;
  }
}

export function isValidCurp(value: string): boolean {
  return hasCurpFormat(value.trim().toUpperCase());
}

/** Keep only backend errors with a matching identity-form field name. */
export function normalizeIDFieldErrors(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};

  const errors: Record<string, string> = {};
  for (const [rawField, rawMessage] of Object.entries(value)) {
    const message = typeof rawMessage === "string" ? rawMessage.trim() : "";
    if (ID_FIELD_ERROR_KEYS.has(rawField) && message) errors[rawField] = message;
  }
  return errors;
}

export function validateIDData(
  data: unknown,
  options: IDDataValidationOptions = {
    requireDialCode: true,
    requireStrictCurp: true,
    requireExteriorNumber: true,
  },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const record = isRecord(data) ? data : {};
  const direccion = isRecord(record.direccion) ? record.direccion : {};
  // Invitation email and phone are read-only. Their format must not block
  // editing identity/address; the server owns BDC contact checks.
  for (const field of REQUIRED_IDENTITY_FIELDS) {
    if (!readRequiredValue(record, field)) {
      errors[field] = REQUIRED_FIELD_ERROR;
    }
  }

  if (options.strictBdcValidation) {
    const firstName = normalizeBdcName(
      [record.nombre, record.segundoNombre]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
        .join(" "),
    );
    if (firstName && !BDC_NAME_PATTERN.test(firstName)) {
      errors.nombre = BDC_NAME_FORMAT_ERROR;
    }

    for (const field of ["apellidoPaterno", "apellidoMaterno"] as const) {
      const value = normalizeBdcName(readRequiredValue(record, field));
      if (
        value &&
        !(field === "apellidoMaterno" && isSecondSurnameNotProvided(value)) &&
        !BDC_NAME_PATTERN.test(value)
      ) {
        errors[field] = BDC_SURNAME_FORMAT_ERROR;
      }
    }

    // The RFC in this identity form is sent to BDC only for the natural
    // person flow. PM RFC data belongs to the company-data form instead.
    if (options.requireBirthDate) {
      const rfc = readRequiredValue(record, "rfc").toUpperCase();
      if (!rfc) errors.rfc = BDC_RFC_REQUIRED_ERROR;
      else if (!BDC_PHYSICAL_PERSON_RFC_PATTERN.test(rfc)) {
        errors.rfc = BDC_RFC_FORMAT_ERROR;
      }
      validateBdcAddress(direccion, errors);
    }
  }

  if (options.requireBirthDate) {
    const birthDate = readRequiredValue(record, "fechaNacimiento");
    if (!birthDate) errors.fechaNacimiento = REQUIRED_FIELD_ERROR;
    else if (!isValidBirthDate(birthDate))
      errors.fechaNacimiento = BIRTH_DATE_FORMAT_ERROR;
  }

  if (
    options.requireStrictCurp !== false &&
    !errors.curp &&
    !isValidCurp(readRequiredValue(record, "curp"))
  ) {
    errors.curp = CURP_FORMAT_ERROR;
  }

  if (
    options.requireDialCode &&
    !isValidNipDestinationDialCode(record.dial_code)
  ) {
    errors.dial_code = DIAL_CODE_FORMAT_ERROR;
  }

  for (const field of REQUIRED_ADDRESS_FIELDS) {
    if (field === "numeroExterior" && options.requireExteriorNumber === false) {
      continue;
    }
    if (!readRequiredValue(direccion, field)) {
      errors[`direccion.${field}`] = REQUIRED_FIELD_ERROR;
    }
  }

  return errors;
}

export function hasValidIDData(
  data: unknown,
  options: IDDataValidationOptions = {
    requireDialCode: true,
    requireStrictCurp: true,
    requireExteriorNumber: true,
  },
): boolean {
  if (!data || typeof data !== "object") return false;
  return Object.keys(validateIDData(data, options)).length === 0;
}

export function hasReviewedNipDestination(data: unknown): boolean {
  const record = isRecord(data) ? data : null;

  return (
    !!record &&
    record[NIP_DESTINATION_REVIEWED_FIELD] === true &&
    isValidNipDestinationDialCode(record[NIP_DESTINATION_DIAL_CODE_FIELD]) &&
    isValidNipDestinationDialCode(record.dial_code)
  );
}

export function isDataValidationSubmittedWorkflowStep(
  workflowStep: string | null | undefined,
): boolean {
  return workflowStep == null || workflowStep === "completed";
}
